import React, { useState, useCallback, useMemo } from 'react'

import { EditOutlined, CheckOutlined, RedoOutlined, MinusCircleOutlined, PlusCircleOutlined } from '@ant-design/icons'
import classnames from 'classnames'
import Image from 'next/image'
import { Form, Input, Button, Modal, message } from 'antd'
import http from '@/utils/request'

import { parseStream } from '@/utils/tryParse'
import tryJson from '@/utils/tryJson'
import useReducers from '@/store/long-creation'

import useCheckLongCreationPending from '@/hooks/useCheckLongCreationPending'
import styles from './Outline.module.scss'
import commonStyles from '../../long-creation.module.scss'
import sendIcon from '@/assets/send.png'

export default React.memo(function Outline() {
  const [modifyVisible, setModifyVisible] = useState(false)
  const [state, actions] = useReducers()
  const [loading, setLoading] = useState(false)
  const [form] = Form.useForm()

  useCheckLongCreationPending('OUTLINE')

  const messages = useMemo(() => {
    return state.messages[state.active]
  }, [state.messages, state.active])

  const pickOutline = useCallback(async outline => {
    setLoading(true)
    const chatId = state.active

    await actions.pickOutline({
      chatId,
      outline
    })
    setLoading(false)
  }, [state.active])

  const reCreateOutline = useCallback(async () => {
    setLoading(true)
    await actions.reCreateOutline(state.active)
    setLoading(false)
  }, [state.active])

  const modifyOutline = useCallback(async (outline) => {
    setModifyVisible(true)
    form.setFieldsValue({
      outline
    })
  }, [])

  const confirmModify = useCallback(async () => {
    const values = await form.validateFields()
    if (values.outline.filter(Boolean).length === 0) {
      message.info('请至少填写一个大纲')
      return
    }

    setLoading(true)
    await pickOutline(values.outline)
    setLoading(false)
  }, [pickOutline])

  const pickedOutline = useMemo(() => {
    const message = messages?.find(item => item.type === 'OUTLINE' && item.role === 'user')

    if (!message) {
      return null
    }

    const outline = tryJson(message.content)

    return (
      <div className={classnames(commonStyles.message, commonStyles.user)}>
        <div className={styles.outline}>
          <h3>已选大纲</h3>
          {
            outline.map((line, lineIndex) => {
              return (
                <p key={lineIndex}>{lineIndex + 1}. {line}</p>
              )
            })
          }
        </div>
      </div>
    )
  }, [messages])

  const outline = useMemo(() => {
    const message = messages?.find(item => item.type === 'OUTLINE' && item.role === 'assistant')

    if (!message) {
      return null
    }

    if (message.status === 'PENDING') {
      return (
        <div className={classnames(commonStyles.message, commonStyles.assistant)}>
          正在生成大纲...
        </div>
      )
    }

    if (message.status === 'REMOTE_ERROR') {
      return (
        <div className={classnames(commonStyles.message, commonStyles.assistant)}>
          <div className={styles.error}>
            大纲生成失败，请重试 <Button loading={loading} type="primary" size="small" onClick={reCreateOutline}><RedoOutlined/>重新生成</Button>
          </div>
        </div>
      )
    }

    const outlines = tryJson(message.content)

    return (
      <div className={classnames(commonStyles.message, commonStyles.assistant)}>
        {
          outlines.map((outline, index) => {
            return (
              <div key={index} className={styles.outline}>
                <h3>推荐大纲{index + 1}</h3>
                {
                  outline.map((line, lineIndex) => {
                    return (
                      <p key={lineIndex}>{lineIndex + 1}. {line}</p>
                    )
                  })
                }
                {
                  !pickedOutline &&
                  <div className={styles.actions}>
                    <Button type="primary" size="small" onClick={() => modifyOutline(outline)}><EditOutlined/>修改大纲</Button>
                    <Button type="primary" size="small" onClick={() => pickOutline(outline)}><CheckOutlined/>使用大纲</Button>
                  </div>
                }
              </div>
            )
          })
        }

        {
          modifyVisible
        }
        <Modal
          visible={modifyVisible}
          title="修改大纲"
          onCancel={() => setModifyVisible(false)}
          onOk={confirmModify}
          okText="确认"
          cancelText="取消"
          confirmLoading={loading}
        >
          <Form className={styles.outlineForm} form={form} initialValues={{ outline: []}}>
            <Form.List name="outline">
              {
                (fields, operation) =>
                  fields.map(field => {
                    return (
                      <React.Fragment key={field.key}>
                        <div className={styles.formItem}>
                          <Form.Item {...field}>
                            <Input maxLength={20} placeholder="请输入大纲"/>
                          </Form.Item>
                          {
                            fields.length > 1 &&
                            <Button size="small" className={styles.delBtn} onClick={() => operation.remove(field.name)}>
                              <MinusCircleOutlined />
                            </Button>
                          }
                        </div>
                        {
                          fields.length -1 === field.name &&
                          <Button className={styles.addBtn} block type="primary" onClick={() => operation.add('')}><PlusCircleOutlined />添加大纲</Button>
                        }
                      </React.Fragment>
                    )
                  })

              }
            </Form.List>
          </Form>
        </Modal>
      </div>
    )
  }, [messages, pickOutline, pickedOutline, reCreateOutline, loading, modifyVisible])

  return (
    <>
      {outline}
      {pickedOutline}
    </>
  )
  // return (
  //     {outline}
  // )
})
